import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { setAuthHeader, clearAuthHeader } from '../../services/api';
import { clearAllUserData } from '../../utils/logout';
import {
  TWOFA_REMEMBER_ME_KEY,
  applyPendingTwoFactorRememberMe,
  handleRememberMeOnLogin,
  restoreRememberMeAuthToSession,
  syncRememberMeAuthFromSession,
} from '../../utils/rememberMe';
import { clearPreservedBookingState } from '../../utils/sessionManager';
import {
  getPendingSuspiciousActivity,
  respondToSuspiciousActivityAlert,
} from '../../services/suspiciousActivityService';

restoreRememberMeAuthToSession();

console.log('🚀 AUTH SLICE LOADED - CODE IS UPDATED!');
console.log('🔧 setAuthHeader function:', typeof setAuthHeader);
console.log('🔧 clearAuthHeader function:', typeof clearAuthHeader);

// Helper function to safely parse JSON from sessionStorage
const getStoredUser = () => {
  try {
    const storedUser = sessionStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    sessionStorage.removeItem('user'); // Clear corrupted data
    return null;
  }
};

// Helper function to check if token exists and is valid format
const getStoredToken = () => {
  const token = sessionStorage.getItem('token');
  return token && token.length > 0 ? token : null;
};

const isSuspiciousRole = (user) =>
  user?.user_type === 'JetSetter' || user?.user_type === 'Airliner';

// Async thunks for API calls
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password, rememberMe = false }, { rejectWithValue }) => {
    try {
      let pushPayload = {};
      try {
        const { getWebPushCredentialsForAuth } = await import('../../services/pushService');
        const creds = await getWebPushCredentialsForAuth();
        if (creds) {
          pushPayload = creds;
        }
      } catch {
        // Non-blocking
      }

      const response = await api.post('/auth/login', { email, password, ...pushPayload });
      // Response is already unwrapped by interceptor, so response = { success, message, data, twofa_type }
      if (response.success) {
        const { user, token, refreshToken, redirect_url, questions, phone } = response.data;
        const { twofa_type } = response;
        
        console.log('📋 Login response:', { user, token, refreshToken, redirect_url, questions, twofa_type, phone });
        console.log('📋 Questions:', questions);
        if (questions && questions.length > 0) {
          console.log('📋 First question:', questions[0]);
          console.log('📋 First question ID:', questions[0].id);
        }
        
        // If 2FA is required, return 2FA data instead of logging in
        if (twofa_type === 'security_que' && questions) {
          sessionStorage.setItem(TWOFA_REMEMBER_ME_KEY, rememberMe ? 'true' : 'false');
          return {
            user,
            token: null,
            refreshToken: null,
            redirect_url: null,
            twofa_type: 'security_que',
            questions: questions,
            email: email,
            password: password,
            requires2FA: true
          };
        }

        // If OTP 2FA is required (email or sms)
        if ((twofa_type === 'email' || twofa_type === 'sms') && user) {
          sessionStorage.setItem(TWOFA_REMEMBER_ME_KEY, rememberMe ? 'true' : 'false');
          return {
            user,
            token: null,
            refreshToken: null,
            redirect_url: null,
            twofa_type: twofa_type,
            phone: phone,
            email: email,
            password: password,
            requires2FA: true
          };
        }
        
        // Store token and user data only if no 2FA required
        if (token && refreshToken) {
          sessionStorage.setItem('token', token);
          sessionStorage.setItem('refreshToken', refreshToken);
          sessionStorage.setItem('user', JSON.stringify(user));
          handleRememberMeOnLogin(rememberMe, { token, refreshToken, user });
          // Set auth header for all future requests
          setAuthHeader(token);
          console.log('✅ Login successful - Token set in axios defaults');
          
          // Dispatch custom event to notify currency context of user change
          window.dispatchEvent(new CustomEvent('userChanged', { detail: { userId: user.id } }));
        }
        
        // Check if password change is required (set by admin)
        const requiresPasswordChange = response.data.requires_password_change || user.requires_password_change || false;
        
        return { 
          user, 
          token, 
          refreshToken, 
          redirect_url, 
          requires2FA: false,
          requiresPasswordChange,
          suspicious_activity: isSuspiciousRole(user)
            ? response.data?.suspicious_activity ?? null
            : null,
        };
      } else {
        // Pass account_status so the UI can distinguish self-deactivated vs admin-disabled
        return rejectWithValue({
          message: response.message || 'Login failed',
          account_status: response.account_status || null
        });
      }
    } catch (error) {
      // Handle both API error responses and network errors
      // error.data contains the full response body from the interceptor
      const errorMessage = error.data?.message || error.message || 'Login failed';
      const accountStatus = error.data?.account_status || null;
      return rejectWithValue({ message: errorMessage, account_status: accountStatus });
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', userData);

      if (response.success) {
        // Registration successful - user needs to verify email
        // Don't store token/user data yet, wait for email verification
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      let logoutBody = {};
      try {
        const { buildLogoutPushBody } = await import('../../services/pushService');
        logoutBody = buildLogoutPushBody();
      } catch {
        // Non-blocking
      }

      await api.post('/auth/logout', logoutBody);

      clearAllUserData();
      clearPreservedBookingState();
      clearAuthHeader();

      return true;
    } catch (error) {
      clearAllUserData();
      clearPreservedBookingState();
      clearAuthHeader();

      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getUserProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/profile');

      if (response.success) {
        return response.data.user;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile', profileData);

      if (response.success) {
        const storedUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        const updatedUser = { ...storedUser, ...response.data.user };
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      // Handle validation errors
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserProfileImage = createAsyncThunk(
  'auth/updateProfileImage',
  async (imageData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/profile/image', { crop_image_text: imageData });

      if (response.success) {
        // Update user data in sessionStorage with new image
        const storedUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        const updatedUser = { 
          ...storedUser, 
          image: response.data.user.image,
          image_url: response.data.user.image_url
        };
        sessionStorage.setItem('user', JSON.stringify(updatedUser));

        return {
          user: response.data.user,
          image_url: response.data.user.image_url
        };
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ current_password, password, new_password }, { rejectWithValue }) => {
    try {
      const finalNewPassword = new_password || password;
      const response = await api.post('/auth/change-password', {
        current_password,
        password: finalNewPassword,
      });

      if (response.success) {
        return response.message;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/verify-email/${token}?email=${encodeURIComponent(email)}`);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const setupPassword = createAsyncThunk(
  'auth/setupPassword',
  async ({ token, email, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/setup-password', {
        token,
        email,
        password,
        password_confirmation
      });

      if (response.success) {
        return response;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });

      if (response.success) {
        return response;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const validateResetToken = createAsyncThunk(
  'auth/validateResetToken',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/validate-reset-token/${token}?email=${encodeURIComponent(email)}`);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, email, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        password,
        password_confirmation
      });

      if (response.success) {
        return response;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

// 2FA Thunks
export const enable2FA = createAsyncThunk(
  'auth/enable2FA',
  async ({ method, security_questions }, { rejectWithValue }) => {
    try {
      const payload = { method };
      if (security_questions) {
        payload.security_questions = security_questions;
      }
      
      const response = await api.post('/2fa/enable', payload);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const disable2FA = createAsyncThunk(
  'auth/disable2FA',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/2fa/disable');

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const request2FAOTP = createAsyncThunk(
  'auth/request2FAOTP',
  async ({ email, method = 'email' }, { rejectWithValue }) => {
    try {
      const endpoint = method === 'sms' ? '/2fa/request-sms-otp' : '/2fa/request-email-otp';
      const payload = method === 'sms' ? { phone_number: email } : { email };
      
      const response = await api.post(endpoint, payload);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verify2FAOTP = createAsyncThunk(
  'auth/verify2FAOTP',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      let pushPayload = {};
      try {
        const { getWebPushCredentialsForAuth } = await import('../../services/pushService');
        const creds = await getWebPushCredentialsForAuth();
        if (creds) {
          pushPayload = creds;
        }
      } catch {
        // Non-blocking
      }

      const response = await api.post('/2fa/verify-otp', { email, otp, ...pushPayload });

      if (response.success) {
        // Store tokens from 2FA verification
        const { access_token, refresh_token } = response.data;
        if (access_token && refresh_token) {
          sessionStorage.setItem('token', access_token);
          sessionStorage.setItem('refreshToken', refresh_token);
          setAuthHeader(access_token);
          applyPendingTwoFactorRememberMe({
            token: access_token,
            refreshToken: refresh_token,
            user: response.data.user,
          });
        }
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const resend2FAOTP = createAsyncThunk(
  'auth/resend2FAOTP',
  async ({ email, method = 'email' }, { rejectWithValue }) => {
    try {
      const response = await api.post('/2fa/resend-otp', { email, method });

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verify2FASecurityQuestion = createAsyncThunk(
  'auth/verify2FASecurityQuestion',
  async ({ email, qId, answer }, { rejectWithValue }) => {
    try {
      let pushPayload = {};
      try {
        const { getWebPushCredentialsForAuth } = await import('../../services/pushService');
        const creds = await getWebPushCredentialsForAuth();
        if (creds) {
          pushPayload = creds;
        }
      } catch {
        // Non-blocking
      }

      const response = await api.post('/2fa/verify-security-questions', {
        email,
        qId,
        answer,
        ...pushPayload
      });

      if (response.success) {
        // Store tokens from 2FA verification
        const { token, refreshToken } = response.data;
        if (token && refreshToken) {
          sessionStorage.setItem('token', token);
          sessionStorage.setItem('refreshToken', refreshToken);
          setAuthHeader(token);
          applyPendingTwoFactorRememberMe({
            token,
            refreshToken,
            user: response.data.user,
          });
        }
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const get2FASecurityQuestions = createAsyncThunk(
  'auth/get2FASecurityQuestions',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/2fa/security-questions?email=${encodeURIComponent(email)}`);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSuspiciousActivity = createAsyncThunk(
  'auth/fetchSuspiciousActivity',
  async (_, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.auth?.user;
      if (!isSuspiciousRole(user)) {
        return null;
      }
      const response = await getPendingSuspiciousActivity();
      if (response.success) {
        return response.data?.suspicious_activity ?? null;
      }
      return rejectWithValue(response.message || 'Failed to fetch suspicious activity');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch suspicious activity');
    }
  }
);

export const respondToSuspiciousActivity = createAsyncThunk(
  'auth/respondToSuspiciousActivity',
  async ({ alert_id, response }, { rejectWithValue }) => {
    try {
      const apiResponse = await respondToSuspiciousActivityAlert({ alert_id, response });
      if (apiResponse.success) {
        return { response, message: apiResponse.message };
      }
      return rejectWithValue(apiResponse.message || 'Failed to submit response');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to submit response');
    }
  }
);

// Initial state
const initialState = {
  user: getStoredUser(),
  token: getStoredToken(),
  refreshToken: sessionStorage.getItem('refreshToken') || null,
  isAuthenticated: !!(getStoredToken() && getStoredUser()),
  loading: false,
  error: null,
  profileLoading: false,
  passwordChangeLoading: false,
  twoFALoading: false,
  redirectUrl: null, // Add redirect URL for role-based navigation
  isInitialized: true, // Track if auth state has been initialized
  resetData: null, // Store reset password data (token, email, resetUrl)
  twofa_type: null, // 2FA type if required
  twofa_questions: null, // Security questions for 2FA
  twofa_email: null, // Email for 2FA verification
  twofa_password: null, // Password for 2FA verification
  twofa_phone: null, // Phone number for SMS 2FA
  isInactive: false, // Track if account is inactive
  requiresPasswordChange: false, // Track if user must change password (admin reset)
  suspiciousActivity: null,
  suspiciousActivityLoading: false,
  suspiciousActivityResponding: false,
  suspiciousActivityError: null,
  postAuthRedirect: null,
};

// Debug: Log initial auth state
// console.log('🔐 Auth Initial State:', {
//   hasUser: !!initialState.user,
//   hasToken: !!initialState.token,
//   isAuthenticated: initialState.isAuthenticated,
//   userEmail: initialState.user?.email,
//   userType: initialState.user?.user_type,
//   isInitialized: initialState.isInitialized
// });

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.redirectUrl = null;
      state.resetData = null;
      state.isInitialized = true;
      state.isInactive = false;
      state.suspiciousActivity = null;
      state.suspiciousActivityError = null;
      state.suspiciousActivityLoading = false;
      state.suspiciousActivityResponding = false;
      state.postAuthRedirect = null;

      // Clear all user data from storage
      clearAllUserData();

      // Clear auth header
      clearAuthHeader();
    },
    setCredentials: (state, action) => {
      const { user, token, refreshToken } = action.payload;
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.isInitialized = true;
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('refreshToken', refreshToken);
      sessionStorage.setItem('user', JSON.stringify(user));
      applyPendingTwoFactorRememberMe({ token, refreshToken, user });
      syncRememberMeAuthFromSession();
      // Set auth header for all future requests
      setAuthHeader(token);
    },
    clearRedirectUrl: (state) => {
      state.redirectUrl = null;
    },
    clear2FAState: (state) => {
      state.twofa_type = null;
      state.twofa_questions = null;
      state.twofa_email = null;
      state.twofa_password = null;
      state.twofa_phone = null;
    },
    clearPasswordChangeFlag: (state) => {
      state.requiresPasswordChange = false;
    },
    setSuspiciousActivity: (state, action) => {
      state.suspiciousActivity = action.payload || null;
      state.suspiciousActivityError = null;
    },
    clearSuspiciousActivity: (state) => {
      state.suspiciousActivity = null;
      state.suspiciousActivityError = null;
    },
    setPostAuthRedirect: (state, action) => {
      state.postAuthRedirect = action.payload || null;
    },
    clearPostAuthRedirect: (state) => {
      state.postAuthRedirect = null;
    },
    // Helper function to get redirect URL based on user type
    getRedirectUrlForUserType: (state) => {
      if (!state.user?.user_type) return '/';

      switch (state.user.user_type) {
        case 'Admin':
        case 'SubAdmin':
          return '/admin'; // Admin panel on same app
        case 'Airliner':
          return '/airliner/profile'; // Internal route within React app
        case 'JetSetter':
          return '/jetsetter/profile/user-details';
        default:
          return '/'; // Default to search page
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.redirectUrl = null;
        state.suspiciousActivityError = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        
        // Check if 2FA is required
        if (action.payload.requires2FA) {
          state.twofa_type = action.payload.twofa_type;
          state.twofa_questions = action.payload.questions;
          state.twofa_email = action.payload.email;
          state.twofa_password = action.payload.password;
          state.twofa_phone = action.payload.phone;
          state.error = null;
          state.isInitialized = true;
        } else {
          // Normal login flow
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.error = null;
          state.redirectUrl = action.payload.redirect_url;
          state.isInitialized = true;
          state.requiresPasswordChange = action.payload.requiresPasswordChange || false;
          if (isSuspiciousRole(action.payload.user)) {
            state.suspiciousActivity = action.payload.suspicious_activity || null;
          } else {
            state.suspiciousActivity = null;
          }
          // Clear 2FA data
          state.twofa_type = null;
          state.twofa_questions = null;
          state.twofa_email = null;
          state.twofa_password = null;
          state.twofa_phone = null;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Login failed';
        state.isAuthenticated = false;
        state.redirectUrl = null;
        state.isInitialized = true;
        // Store inactive status — only show reactivation button for self-deactivated accounts
        state.isInactive = action.payload?.account_status === 'self_deactivated';
      })

      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Don't set user as authenticated yet - they need to verify email
        // Just show success message
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        // Reset all state to initial values
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        state.redirectUrl = null;
        state.profileLoading = false;
        state.passwordChangeLoading = false;
        state.isInitialized = true;
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
        state.twofa_phone = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout fails, clear the state
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.redirectUrl = null;
        state.profileLoading = false;
        state.passwordChangeLoading = false;
        state.isInitialized = true;
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
        state.twofa_phone = null;
      })

      // Get Profile
      .addCase(getUserProfile.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = action.payload;
        sessionStorage.setItem('user', JSON.stringify(action.payload));
        syncRememberMeAuthFromSession();
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload;
      })

      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = { ...state.user, ...action.payload };
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload;
      })

      // Update Profile Image
      .addCase(updateUserProfileImage.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(updateUserProfileImage.fulfilled, (state, action) => {
        state.profileLoading = false;
        // Update user image and image_url in state
        if (state.user) {
          state.user = { 
            ...state.user, 
            image: action.payload.user.image,
            image_url: action.payload.image_url
          };
          // Also update sessionStorage
          sessionStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(updateUserProfileImage.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload;
      })

      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.passwordChangeLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordChangeLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordChangeLoading = false;
        state.error = action.payload;
      })

      // Email Verification
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Email verified successfully, but user still needs to set password
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Password Setup
      .addCase(setupPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setupPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Password setup complete - user can now login
      })
      .addCase(setupPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Store the reset data for potential use
        state.resetData = action.payload;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Validate Reset Token
      .addCase(validateResetToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateResetToken.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(validateResetToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.resetData = null; // Clear reset data after successful password reset
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Enable 2FA
      .addCase(enable2FA.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(enable2FA.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        // Update user 2FA status
        if (state.user) {
          state.user.Twofa = action.payload.method;
          sessionStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(enable2FA.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Disable 2FA
      .addCase(disable2FA.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(disable2FA.fulfilled, (state) => {
        state.twoFALoading = false;
        state.error = null;
        // Clear user 2FA status
        if (state.user) {
          state.user.Twofa = null;
          sessionStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(disable2FA.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Request 2FA OTP
      .addCase(request2FAOTP.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(request2FAOTP.fulfilled, (state) => {
        state.twoFALoading = false;
        state.error = null;
      })
      .addCase(request2FAOTP.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Verify 2FA OTP
      .addCase(verify2FAOTP.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(verify2FAOTP.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        // Complete login after 2FA verification
        state.user = action.payload.user || action.payload;
        state.token = action.payload.access_token || action.payload.token;
        state.refreshToken = action.payload.refresh_token || action.payload.refreshToken;
        state.isAuthenticated = true;
        state.redirectUrl = action.payload.redirect_url;
        if (isSuspiciousRole(action.payload.user || action.payload)) {
          state.suspiciousActivity = action.payload.suspicious_activity || null;
        } else {
          state.suspiciousActivity = null;
        }
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
      })
      .addCase(verify2FAOTP.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Resend 2FA OTP
      .addCase(resend2FAOTP.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(resend2FAOTP.fulfilled, (state) => {
        state.twoFALoading = false;
        state.error = null;
      })
      .addCase(resend2FAOTP.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Verify 2FA Security Question
      .addCase(verify2FASecurityQuestion.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(verify2FASecurityQuestion.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        // Complete login after 2FA verification
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.redirectUrl = action.payload.redirect_url;
        if (isSuspiciousRole(action.payload.user || action.payload)) {
          state.suspiciousActivity = action.payload.suspicious_activity || null;
        } else {
          state.suspiciousActivity = null;
        }
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
      })
      .addCase(verify2FASecurityQuestion.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Get 2FA Security Questions
      .addCase(get2FASecurityQuestions.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(get2FASecurityQuestions.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        state.twofa_questions = action.payload;
      })
      .addCase(get2FASecurityQuestions.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Suspicious activity
      .addCase(fetchSuspiciousActivity.pending, (state) => {
        state.suspiciousActivityLoading = true;
        state.suspiciousActivityError = null;
      })
      .addCase(fetchSuspiciousActivity.fulfilled, (state, action) => {
        state.suspiciousActivityLoading = false;
        state.suspiciousActivity = action.payload;
      })
      .addCase(fetchSuspiciousActivity.rejected, (state, action) => {
        state.suspiciousActivityLoading = false;
        state.suspiciousActivityError = action.payload || 'Failed to fetch suspicious activity';
      })
      .addCase(respondToSuspiciousActivity.pending, (state) => {
        state.suspiciousActivityResponding = true;
        state.suspiciousActivityError = null;
      })
      .addCase(respondToSuspiciousActivity.fulfilled, (state) => {
        state.suspiciousActivityResponding = false;
        state.suspiciousActivity = null;
        state.suspiciousActivityError = null;
      })
      .addCase(respondToSuspiciousActivity.rejected, (state, action) => {
        state.suspiciousActivityResponding = false;
        state.suspiciousActivityError = action.payload || 'Failed to submit response';
      });
  },
});

export const {
  clearError,
  clearAuth,
  setCredentials,
  clearRedirectUrl,
  clear2FAState,
  clearPasswordChangeFlag,
  setSuspiciousActivity,
  clearSuspiciousActivity,
  setPostAuthRedirect,
  clearPostAuthRedirect,
} = authSlice.actions;
export default authSlice.reducer;